import React, { useState } from 'react';
import { View, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { translate } from '../../../services/translate';
import { faChevronDown, faShield } from '@fortawesome/free-solid-svg-icons';
import MyAppText from '../../../components/MyAppText';
import moment from 'moment';
import { Pagination } from '../../../components/Pagination';
import { dispatchService } from '../../../services/central-api/dispatch';
import MyDataTable from '../../../components/MyDataTable';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { DispatchParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import OccurrencesFilter from './Filters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import CustomButton from '../../../components/CustomButton';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import getBestContrastColor from '../../../services/best-contrast-color';
import { useAuth } from '../../../context/Auth';
import BulkFinishModal from './BulkFinishModal';
import DropdownButton, { Action } from '../../../components/DropdownButton';
import { ClientError } from '../../../services/central-api/base-service';
import Toast from 'react-native-toast-message';
import TouchableOpacityWithMiddleClick from '../../../components/TouchableOpacityWithMiddleClick';
import { useQuery } from '@tanstack/react-query';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import Badge from '../../../components/Badge';

export default function Occurrences({ navigation }: { navigation: Navigation; }) {
    const { styles } = useStyles(stylesheet);
    const route = useRoute<RouteProp<DispatchParamList, 'Occurrences'>>();
    const { user: authenticatedUser } = useAuth();
    const [showBulkFinishModal, setShowBulkFinishModal] = useState(false);

    const [filters, setFilters] = useFilters<DispatchParamList['Occurrences']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });

    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getOccurrences', filters],
        queryFn: () => dispatchService.getOccurrences(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    function getTitle(occurrence: PaginatedOccurrence) {
        if (occurrence.triggerType == null && occurrence.occurrenceType) {
            return occurrence.occurrenceType.name;
        } else if (occurrence.triggerType == 'face_detected') {
            return translate('faceDetectedAlert');
        } else if (occurrence.triggerType == 'missing_face_detected') {
            return translate('missingFaceDetectedAlert');
        } else if (occurrence.triggerType == 'mp_face_detected') {
            return translate('mpFaceDetectedAlert');
        } else if (occurrence.triggerType == 'plate_detected') {
            return translate('plateDetectedAlert');
        } else if (occurrence.triggerType == 'plate_detected_by_mobile_agent') {
            return translate('plateDetectedByMobileAgentAlert');
        } else if (occurrence.triggerType == 'plate_detected_by_citizen_app') {
            return translate('plateDetectedByCitizenAppAlert');
        } else if (occurrence.triggerType == 'mp_plate_detected') {
            return translate('mpPlateDetectedAlert');
        } else if (occurrence.triggerType == 'mp_mobile_agent_plate_detected') {
            return translate('mpMobileAgentPlateDetectedAlert');
        } else if (occurrence.triggerType == 'irregular_vehicle') {
            return translate('irregular_vehicle');
        } else if (occurrence.triggerType == 'clone_suspect') {
            return translate('clone_suspect');
        } else if (occurrence.triggerType == 'illegal_debris') {
            return translate('illegalDebrisAlert');
        } else if (occurrence.triggerType == 'area_invasion') {
            return translate('area_invasion');
        } else if (occurrence.triggerType == 'alarm_center') {
            return translate('alarmCenterAlert');
        } else if (occurrence.triggerType == 'guardian_app') {
            return translate('guardianAppAlert');
        } else if (occurrence.triggerType == 'school_app') {
            return translate('schoolAppAlert');
        } else if (occurrence.triggerType == 'sigrc') {
            return translate('sigrcAlert');
        } else if (occurrence.triggerType == 'cortex_plate_detected') {
            return translate('cortexPlateDetectedAlert');
        } else if (['scenechangedetection', 'defocus'].includes(occurrence.triggerType || '')) {
            return translate('cameraDepredationSuspect');
        }
        return '';
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));

            navigation.setParams({
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1
            });
        }
    }

    const renderUnitContent = (row: PaginatedOccurrence) => {
        const renderUnitInfo = () => {
            if (!row.OccurrenceUnits || row.OccurrenceUnits.length === 0) {
                return null;
            }
            return row.OccurrenceUnits.map((unit, i) => {

                return (
                    <View key={i} style={{ flexDirection: 'row', alignItems: 'center', columnGap: 10 }}>
                        <FontAwesomeIcon icon={faShield} fontSize={16} color={'#888888'} />
                        <MyAppText>{unit.unitName.name}</MyAppText> / <MyAppText>{unit.actingBodyUnit.name}</MyAppText>
                        {unit.displaced && <MyAppText>- {translate('displaced')}</MyAppText>}
                    </View>
                );
            });
        };

        return (
            <View style={{ paddingVertical: 10, paddingLeft: 50, flexDirection: 'column', rowGap: 10 }}>
                {renderUnitInfo()}
            </View>
        );
    };

    async function downloadPdf(occurrence: PaginatedOccurrence) {
        if (!occurrence.signableReport) {
            return;
        }
        try {
            const signableReport = await dispatchService.getSignableReport(occurrence.signableReport.id);
            window.open(signableReport.resultUrl);
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return <Container>
        <Header
            title={translate('occurrences')}
            actions={
                <>
                    <DropdownButton
                        isButton={true}
                        buttonLabel={'actions'}
                        buttonIcon={faChevronDown}
                        actions={[{
                            content: translate('bulkFinish'),
                            variant: 'danger',
                            onPress: () => setShowBulkFinishModal(true),
                            omit: !(authenticatedUser?.permissions.dispatch_module_access
                                || authenticatedUser?.permissions.dispatch_manage_occurrences_bulk_finish
                                || authenticatedUser?.permissions.dispatch_manage_all_acting_bodies_occurrences_bulk_finish),
                        }]}
                        offsetX={-97}
                        offsetY={41}
                        size={200}
                    />
                    <CustomButton
                        onPress={() => navigation.navigate('DispatchOccurrenceDetail')}
                        text={translate('register')}
                    />
                </>
            }
        />
        <Card>
            <DataTableHeader
                zIndex={2}
                title={translate('registeredOccurrences')}
                filter={<OccurrencesFilter
                    filters={filters}
                    setFilters={setFilters}
                    textFilter={textFilter}
                    setTextFilter={setTextFilter} />}
                filterInput={<FilterInput
                    value={textFilter ?? ''}
                    onChange={setTextFilter}
                    placeholder={translate('typeToSearch')}
                    onKeyPress={handleKeyDown}

                />}
            />

            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('status'),
                        cell: row => <View style={{ minWidth: 120, justifyContent: 'center', alignItems: 'flex-start' }}>
                            <Badge
                                mode='SQUARE'
                                color={row.situation === 'concluded' ? 'green' :
                                    row.situation === 'in_service' ? 'blue' :
                                        row.situation === 'ongoing' ? 'purple' : 'orange'
                                }
                            >{translate(row.situation)}</Badge>
                        </View>,
                        maxWidth: '130px',
                        minWidth: '130px',
                    },
                    {
                        name: translate('event'),
                        cell: row => <TouchableOpacityWithMiddleClick
                            onPress={() => navigation.navigate('DispatchOccurrenceDetail', { occurrenceId: row.id })}
                            onMiddleClick={() => window.open(`/dispatch/occurrences/detail/${row.id}`, '_blank')}
                        >
                            <MyAppText style={{ textDecorationLine: 'underline' }}>{row.id}</MyAppText>
                        </TouchableOpacityWithMiddleClick>,
                        grow: 0,
                    },
                    {
                        name: translate('team') + ' (' + translate('starter') + ')',
                        selector: row => {
                            if (row.OccurrenceUnits.length === 0) {
                                return '';
                            }
                            const occurrenceUnit = row.OccurrenceUnits.find(ou => ou.unitName.isStarter);

                            if (!occurrenceUnit) {
                                return '';
                            }

                            return occurrenceUnit.unitName.name;
                        },
                        wrap: true,
                        grow: 2
                    },
                    {
                        name: translate('type'),
                        wrap: true,
                        grow: 2,
                        selector: row => getTitle(row),
                    },
                    {
                        name: translate('priority'),
                        wrap: true,
                        cell: row => <MyAppText style={{ color: row.priority === 'maximum' ? '#FF0027' : undefined }}>
                            {row.priority ? translate(row.priority) : ''}
                        </MyAppText>,
                    },
                    {
                        name: translate('start_date'),
                        selector: row => row?.createdAt ? moment(row?.createdAt).format('DD/MM/YYYY HH:mm:ss') : '',
                        wrap: true
                    },
                    {
                        name: translate('end_date'),
                        selector: row => row?.finishedAt ? moment(row?.finishedAt).format('DD/MM/YYYY HH:mm:ss') : '',
                        wrap: true
                    },
                    {
                        name: translate('tags'),
                        cell: row =>
                            <View style={{ flexDirection: 'row', flexWrap: 'wrap', width: '100%', gap: 5, paddingVertical: 5 }}>
                                {row.tags ?
                                    row.tags.map((tag) =>
                                        <View key={tag.id} style={[styles.marker, { backgroundColor: tag.color }]}>
                                            <MyAppText style={styles.markerText(tag.color)}>
                                                {tag.name}
                                            </MyAppText>
                                        </View>
                                    ) : null
                                }</View>
                    },
                    {
                        button: true,
                        cell: row => {

                            const actions: Action[] = [{
                                content: translate('editInformation'),
                                onPress: () => {
                                    navigation.navigate('DispatchOccurrenceDetail', { occurrenceId: row.id });
                                }
                            }, {
                                content: translate('editInformationNewTab'),
                                onPress: () => {
                                    window.open(`/dispatch/occurrences/detail/${row.id}`, '_blank');
                                },
                            }];

                            if (row.signableReport?.reportUrl) {
                                actions.push({
                                    content: translate('exportPDF'),
                                    onPress: () => {
                                        downloadPdf(row);
                                    },
                                });
                            }

                            return <DropdownButton
                                actions={actions}
                            />;
                        }
                    }]}
                    data={data?.rows ?? []}
                    expandableRowsComponent={({ data }) => renderUnitContent(data)}
                    expandableRows
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={data?.count ?? 0}
                    setPage={page => {
                        setFilters(old => ({
                            ...old,
                            page
                        }));

                        navigation.setParams({
                            ...route.params,
                            page: page + 1
                        });
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({
                            ...old,
                            limit: pageSize,
                            page: 0,
                        }));

                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                />}
            />
        </Card>

        <BulkFinishModal
            isModalVisible={showBulkFinishModal}
            setModalVisible={setShowBulkFinishModal}
            onSubmit={() => {
                setShowBulkFinishModal(false);
                refetch();
            }}
        />
    </Container>;
}
const stylesheet = createStyleSheet(theme => ({
    circle: {
        width: 15,
        height: 15,
        borderRadius: 7.5,
    },
    red: {
        backgroundColor: 'red',
    },
    green: {
        backgroundColor: 'green',
    },
    cardList: {
        borderWidth: 1,
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
        borderColor: theme.colors.dataTable.border,
        backgroundColor: theme.colors.dataTable.background,
        paddingHorizontal: 10,
        flex: 1,
    },
    status: {
        flexDirection: 'row',
        alignItems: 'center',
        columnGap: 10,
        width: '100%'
    },
    marker: {
        borderRadius: 8,
        minHeight: 16,
        paddingLeft: 10,
        paddingRight: 10,
        maxWidth: '100%'
    },
    markerText: (tagColor: string) => {
        return {
            fontWeight: '500',
            color: getBestContrastColor(tagColor),
            fontSize: 11,
        };
    },
    buttonsContainer: (isMobile: boolean) => ({
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'flex-end',
        gap: 10,
        width: isMobile ? '100%' : 'auto',
    }),
}));
